import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { downloadTextFile } from '../utils/importTemplates';
import Pagination from '../components/Pagination';
import { parseUserAgent } from '../utils/parseUserAgent';

/** Types d’export admin : `id` = `resource_type` côté API ; `label` = libellé UI. */
const ADMIN_EXPORT_TYPES = [
  { id: 'members', label: 'Membres' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'transactions', label: 'Écritures Ledgera' },
  { id: 'contributions', label: 'Cotisations' },
  { id: 'meetings', label: 'Rapports (réunions)' },
  { id: 'documents', label: 'Documents' },
  { id: 'events', label: 'Événements' },
];

const AdminDashboard = () => {
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsBackendMissing, setSessionsBackendMissing] = useState(false);
  const [terminatingUserId, setTerminatingUserId] = useState('');
  const [systemInfo, setSystemInfo] = useState(null);
  const [systemCollections, setSystemCollections] = useState({});
  const [loading, setLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState({
    action: '',
    resource_type: '',
    startDate: '',
    endDate: ''
  });
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(20);
  const [sessionsPageSize, setSessionsPageSize] = useState(20);

  const auditTotalPages = Math.max(1, Math.ceil(auditTotal / auditPageSize));

  /** KPI finances admin : mêmes libellés que la page Finances (solde / entrées / sorties). */
  const adminFinancialKpi = useMemo(() => {
    const fin = stats?.financial;
    if (!fin) return null;
    const isLedger = fin.balance_source === 'ledger';
    return {
      isLedger,
      soldeActuel: Number(fin.balance || 0),
      totalEntrees: isLedger ? Number(fin.ledger_credits_total ?? 0) : Number(fin.total_contributions ?? 0),
      totalSorties: Number(fin.total_expenses ?? 0),
      cotisationsRef: Number(fin.total_contributions ?? 0),
      tx30: Number(fin.transactions_last_30_days || 0),
      activite30Ledger: fin.activity_last_30_days_source === 'ledger',
    };
  }, [stats]);

  useEffect(() => {
    loadData();
  }, [activeTab, auditFilters, auditPage, sessionsPage, auditPageSize, sessionsPageSize]);

  const getApiErrorDetail = (err) => {
    const d = err?.response?.data?.detail;
    return typeof d === 'string' ? d : '';
  };

  const loadData = async () => {
    if (activeTab === 'export' || activeTab === 'import') {
      return;
    }
    try {
      setLoading(true);
      
      if (activeTab === 'stats') {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } else if (activeTab === 'audit') {
        const params = {
          limit: auditPageSize,
          skip: (auditPage - 1) * auditPageSize
        };
        if (auditFilters.action) params.action = auditFilters.action;
        if (auditFilters.resource_type) params.resource_type = auditFilters.resource_type;
        if (auditFilters.startDate) params.start_date = auditFilters.startDate;
        if (auditFilters.endDate) params.end_date = auditFilters.endDate;
        const response = await adminAPI.getAuditLogs(params);
        setAuditLogs(response.data.logs || []);
        setAuditTotal(response.data.total || 0);
      } else if (activeTab === 'sessions') {
        let backendMissing = false;
        const sessionsRes = await adminAPI.getActiveSessions().catch((err) => {
          if (Number(err?.response?.status) === 404) backendMissing = true;
          return { data: { sessions: [] } };
        });
        const activeUsersRes = await adminAPI
          .getActiveUsers()
          .catch(() => ({ data: { users: [] } }));
        setSessionsBackendMissing(backendMissing);
        setActiveSessions(sessionsRes?.data?.sessions || []);
        setActiveUsers(activeUsersRes?.data?.users || []);
      } else if (activeTab === 'system') {
        const response = await adminAPI.getSystemInfo();
        setSystemInfo(response.data || null);
        setSystemCollections(
          response?.data?.system?.database?.collections ||
          response?.data?.collections ||
          {}
        );
      }
    } catch (err) {
      error('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parseCsvLine = (line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      const next = i + 1 < line.length ? line[i + 1] : '';
      if (ch === '"' && inQuotes && next === '"') {
        cur += '"';
        i += 1;
        continue;
      }
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === ',' && !inQuotes) {
        out.push(cur);
        cur = '';
        continue;
      }
      cur += ch;
    }
    out.push(cur);
    return out;
  };

  const coerceCsvValue = (value) => {
    const s = (value ?? '').toString().trim();
    if (s === '') return null;
    const low = s.toLowerCase();
    if (low === 'true') return true;
    if (low === 'false') return false;
    if (low === 'null' || low === 'none') return null;
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try {
        return JSON.parse(s);
      } catch (_) {
        // keep raw string when not valid JSON
      }
    }
    if (/^-?\d+$/.test(s)) return Number.parseInt(s, 10);
    if (/^-?\d+\.\d+$/.test(s)) return Number.parseFloat(s);
    return s;
  };

  const parseCsvToObjects = (csvText) => {
    const lines = (csvText || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .filter((line) => line.trim() !== '');
    if (lines.length < 2) return [];
    const headers = parseCsvLine(lines[0]).map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cols = parseCsvLine(line);
      const row = {};
      headers.forEach((h, idx) => {
        if (!h) return;
        row[h] = coerceCsvValue(cols[idx]);
      });
      return row;
    });
  };

  const handleExport = async (resourceType, format = 'json') => {
    try {
      const response = await adminAPI.exportData(resourceType, format);
      const isCsv = format === 'csv';
      const payload = isCsv
        ? (response.data?.csv ?? '')
        : JSON.stringify(response.data?.data ?? [], null, 2);
      const dataBlob = new Blob([payload], { type: isCsv ? 'text/csv;charset=utf-8' : 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resourceType}_${new Date().toISOString().split('T')[0]}.${isCsv ? 'csv' : 'json'}`;
      link.click();
      URL.revokeObjectURL(url);
      success(`Données ${resourceType} exportées en ${format.toUpperCase()} avec succès`);
    } catch (err) {
      error('Erreur lors de l\'export des données');
      console.error(err);
    }
  };

  const handleImport = async (resourceType, file) => {
    if (!file) {
      error('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setLoading(true);
      const fileContent = await file.text();
      const isCsv = file.name.toLowerCase().endsWith('.csv');
      const format = isCsv ? 'csv' : 'json';
      const parsedData = isCsv ? parseCsvToObjects(fileContent) : JSON.parse(fileContent);
      if (!Array.isArray(parsedData) || parsedData.length === 0) {
        throw new Error('Fichier vide ou format invalide');
      }
      const dataPayload = isCsv ? fileContent : parsedData;
      
      // Appeler l'API d'import
      const response = await adminAPI.importData(resourceType, dataPayload, format);
      const created = response?.data?.created_count || 0;
      const membersCreated = response?.data?.members_created || 0;
      const usersCreated = response?.data?.users_created || 0;
      const usersLinked = response?.data?.users_linked || 0;
      const details = [];
      if (membersCreated > 0) details.push(`${membersCreated} membre(s)`);
      if (usersCreated > 0) details.push(`${usersCreated} utilisateur(s) créé(s)`);
      if (usersLinked > 0) details.push(`${usersLinked} compte(s) lié(s)`);
      const suffix = details.length > 0 ? ` (${details.join(', ')})` : '';
      
      if (response.data.errors && response.data.errors.length > 0) {
        error(`Import terminé avec ${response.data.errors.length} erreur(s). ${created} élément(s) importé(s)${suffix}.`);
        console.error('Erreurs d\'import:', response.data.errors);
      } else {
        success(`${created} élément(s) importé(s) avec succès${suffix}`);
      }
      
      // Recharger les données si nécessaire
      if (activeTab === 'stats') {
        loadData();
      }
    } catch (err) {
      error('Erreur lors de l\'import des données: ' + (getApiErrorDetail(err) || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDurationSeconds = (totalSeconds) => {
    const n = Number(totalSeconds);
    if (!Number.isFinite(n) || n < 0) return '—';
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = Math.floor(n % 60);
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m > 0) return `${m} min ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
  };

  const handleTerminateSession = async (session) => {
    if (!session?.user_id) return;
    const who = session.display_name || session.username || session.user_id;
    const ok = await confirm({
      title: 'Terminer la session',
      message: `Forcer la déconnexion de ${who} ? Les sessions actives en base seront invalidées.`,
      confirmText: 'Terminer',
      cancelText: 'Annuler',
      type: 'danger',
    });
    if (!ok) return;
    try {
      setTerminatingUserId(session.user_id);
      await adminAPI.terminateUserSessions(session.user_id);
      success(`Session de ${who} terminée.`);
      const refreshed = await adminAPI
        .getActiveSessions()
        .catch(() => ({ data: { sessions: [] } }));
      setActiveSessions(refreshed?.data?.sessions || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      error(typeof detail === 'string' && detail ? detail : 'Échec de la terminaison.');
    } finally {
      setTerminatingUserId('');
    }
  };

  const tabs = [
    { id: 'stats', name: 'Statistiques', icon: '📊' },
    { id: 'audit', name: 'Logs d\'Audit', icon: '📝' },
    { id: 'sessions', name: 'Sessions Actives', icon: '👥' },
    { id: 'system', name: 'Système', icon: '⚙️' },
    { id: 'export', name: 'Export', icon: '💾' },
    { id: 'import', name: 'Import', icon: '📥' }
  ];

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-white via-entrea-panel/35 to-entrea-surface/30 md:bg-white rounded-2xl md:rounded-lg shadow-lg md:shadow-sm border-0 md:border border-gray-200 p-4 sm:p-4 md:p-6 backdrop-blur-sm md:backdrop-blur-none">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-entrea-royal to-entrea-sky bg-clip-text text-transparent md:text-gray-900">Statistiques & audits</h1>
        <p className="text-xs sm:text-sm md:text-base text-gray-600 md:text-gray-600 mt-1 md:mt-2">
          Indicateurs, journaux d&apos;audit, sessions actives et exports/import de données
        </p>
      </div>

      {/* Tabs - Scrollable on mobile */}
      <div className="bg-gradient-to-br from-white via-entrea-panel/35 to-entrea-surface/30 md:bg-white rounded-2xl md:rounded-lg shadow-lg md:shadow-sm border-0 md:border border-gray-200 sticky top-[57px] md:top-0 z-30 backdrop-blur-sm md:backdrop-blur-none">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-1 md:space-x-8 px-3 md:px-6 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-entrea-royal text-entrea-royal'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm flex items-center gap-1.5 md:gap-2 flex-shrink-0 min-w-fit`}
                >
                  <span className="text-base md:text-lg">{tab.icon}</span>
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal"></div>
            </div>
          )}

          {!loading && activeTab === 'stats' && stats && (
            <div className="space-y-4 md:space-y-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Statistiques Détaillées</h2>
              
              {/* Users Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                <div className="relative bg-gradient-to-br from-blue-50/80 via-entrea-panel/50 to-blue-50/80 md:bg-blue-50 rounded-2xl md:rounded-lg p-4 md:p-4 border-0 md:border border-blue-200 shadow-xl md:shadow-sm overflow-hidden">
                  {/* Decorative circle */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-400/20 rounded-full blur-2xl md:hidden"></div>
                  <div className="relative">
                    <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                      </svg>
                      Utilisateurs
                    </h3>
                    <div className="mt-2">
                      <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-entrea-royal bg-clip-text text-transparent md:text-blue-600">{stats.users?.total || 0}</p>
                      <p className="text-sm text-blue-700 font-medium">{stats.users?.active || 0} actifs</p>
                    </div>
                  {stats.users?.by_role && (
                    <div className="mt-4 space-y-1">
                      {Object.entries(stats.users.by_role)
                        .filter(([role]) => role !== 'null' && role !== null)
                        .map(([role, count]) => (
                          <div key={role} className="flex justify-between text-sm">
                            <span className="text-blue-800 capitalize">{role}:</span>
                            <span className="font-medium text-blue-600">{count}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  </div>
                </div>

                {/* Members Stats */}
                <div className="relative bg-gradient-to-br from-emerald-50/80 via-teal-50/60 to-emerald-50/80 md:bg-green-50 rounded-2xl md:rounded-lg p-4 md:p-4 border-0 md:border border-green-200 shadow-xl md:shadow-sm overflow-hidden">
                  {/* Decorative circle */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-400/20 rounded-full blur-2xl md:hidden"></div>
                  <div className="relative">
                    <h3 className="text-sm font-bold text-green-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                      </svg>
                      Membres
                    </h3>
                    <div className="mt-2">
                      <p className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent md:text-green-600">{stats.members?.total || 0}</p>
                      <p className="text-sm text-green-700 font-medium">{stats.members?.active || 0} actifs</p>
                    </div>
                  {stats.members?.by_commission && (
                    <div className="mt-4 space-y-1">
                      {Object.entries(stats.members.by_commission)
                        .sort(([a], [b]) => {
                          // Mettre "null" ou les valeurs vides à la fin
                          if (a === 'null' || a === null || !a) return 1;
                          if (b === 'null' || b === null || !b) return -1;
                          return a.localeCompare(b);
                        })
                        .map(([commission, count]) => (
                          <div key={commission || 'none'} className="flex justify-between text-sm">
                            <span className="text-green-800">{commission && commission !== 'null' ? commission : 'Simple membre'}:</span>
                            <span className="font-medium text-green-600">{count}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  </div>
                </div>

                {/* Financial Stats */}
                <div className="relative bg-gradient-to-br from-entrea-panel/80 via-entrea-surface/60 to-entrea-panel/80 md:bg-entrea-panel rounded-2xl md:rounded-lg p-4 md:p-4 border-0 md:border border-entrea-sky/40 shadow-xl md:shadow-sm overflow-hidden">
                  {/* Decorative circle */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-400/20 rounded-full blur-2xl md:hidden"></div>
                  <div className="relative">
                    <h3 className="text-sm font-bold text-entrea-ink mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                      </svg>
                      Finances
                    </h3>
                    {adminFinancialKpi && (
                      <div className="mt-1 space-y-3 text-sm">
                        <div>
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Solde actuel</p>
                          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-entrea-royal to-entrea-sky bg-clip-text text-transparent md:text-entrea-ink tabular-nums">
                            {adminFinancialKpi.soldeActuel.toLocaleString('fr-FR')}{' '}
                            <span className="text-base font-semibold text-slate-500">FBU</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {adminFinancialKpi.isLedger
                              ? 'Entrées Ledgera moins sorties Ledgera.'
                              : 'Cotisations Entrea payées moins dépenses (données historiques Mongo).'}
                          </p>
                        </div>
                        <div className="border-t border-entrea-sky/30 pt-2">
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total des entrées</p>
                          <p className="text-lg font-semibold text-purple-900 tabular-nums">
                            {adminFinancialKpi.totalEntrees.toLocaleString('fr-FR')} FBU
                          </p>
                          {adminFinancialKpi.isLedger && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Crédits Ledgera. Cotisations Entrea (réf. membres) :{' '}
                              {adminFinancialKpi.cotisationsRef.toLocaleString('fr-FR')} FBU
                            </p>
                          )}
                        </div>
                        <div className="border-t border-entrea-sky/30 pt-2">
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total des sorties</p>
                          <p className="text-lg font-semibold text-purple-900 tabular-nums">
                            {adminFinancialKpi.totalSorties.toLocaleString('fr-FR')} FBU
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {adminFinancialKpi.isLedger ? 'Débits Ledgera.' : 'Dépenses enregistrées (Mongo).'}
                          </p>
                        </div>
                        <p className="text-purple-800 text-xs pt-1 border-t border-entrea-sky/20">
                          Écritures sur 30 j. :{' '}
                          <span className="font-semibold">{adminFinancialKpi.tx30}</span>
                          {adminFinancialKpi.activite30Ledger && (
                            <span className="text-gray-500 font-normal"> (Ledgera)</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Audit Stats */}
              <div className="relative bg-gradient-to-br from-gray-50/80 via-slate-50/60 to-gray-50/80 md:bg-gray-50 rounded-2xl md:rounded-lg p-4 md:p-4 border-0 md:border border-gray-200 shadow-xl md:shadow-sm overflow-hidden">
                {/* Decorative circle */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gray-400/20 rounded-full blur-2xl md:hidden"></div>
                <div className="relative">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
                    </svg>
                    Logs d'Audit
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Total des logs</p>
                      <p className="text-2xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent md:text-gray-900">{stats.audit?.total_logs || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Logs (30 derniers jours)</p>
                      <p className="text-2xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent md:text-gray-900">{stats.audit?.logs_last_30_days || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && activeTab === 'audit' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Logs d'Audit</h2>
              
              {/* Filtres */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
                    <select
                      value={auditFilters.action}
                      onChange={(e) => {
                        setAuditFilters({ ...auditFilters, action: e.target.value });
                        setAuditPage(1);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Toutes les actions</option>
                      <option value="create">Création</option>
                      <option value="update">Modification</option>
                      <option value="delete">Suppression</option>
                      <option value="login">Connexion</option>
                      <option value="logout">Déconnexion</option>
                      <option value="view">Consultation</option>
                      <option value="approve">Approbation</option>
                      <option value="reject">Rejet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Type de ressource</label>
                    <select
                      value={auditFilters.resource_type}
                      onChange={(e) => {
                        setAuditFilters({ ...auditFilters, resource_type: e.target.value });
                        setAuditPage(1);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Tous les types</option>
                      <option value="user">Utilisateur</option>
                      <option value="member">Membre</option>
                      <option value="transaction">Transaction</option>
                      <option value="meeting">Réunion</option>
                      <option value="document">Document</option>
                      <option value="event">Événement</option>
                      <option value="contribution">Cotisation</option>
                      <option value="commission">Commission</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date début</label>
                    <input
                      type="date"
                      value={auditFilters.startDate}
                      onChange={(e) => {
                        setAuditFilters({ ...auditFilters, startDate: e.target.value });
                        setAuditPage(1);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date fin</label>
                    <input
                      type="date"
                      value={auditFilters.endDate}
                      onChange={(e) => {
                        setAuditFilters({ ...auditFilters, endDate: e.target.value });
                        setAuditPage(1);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    setAuditFilters({ action: '', resource_type: '', startDate: '', endDate: '' });
                    setAuditPage(1);
                  }}
                  className="text-sm text-entrea-royal hover:text-entrea-navy font-medium"
                >
                  Réinitialiser les filtres
                </button>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                {auditTotal === 0 ? <span>Aucun log trouvé</span> : null}
              </div>

              {auditTotal > 0 && (
                <Pagination
                  placement="top"
                  currentPage={auditPage}
                  totalPages={auditTotalPages}
                  totalItems={auditTotal}
                  itemsPerPage={auditPageSize}
                  onPageChange={setAuditPage}
                  onPageSizeChange={(n) => {
                    setAuditPageSize(n);
                    setAuditPage(1);
                  }}
                />
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Détails</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">
                            {log.user_name || log.username || `#${String(log.user_id || '').slice(0, 8)}`}
                          </div>
                          {log.username && log.user_name ? (
                            <div className="text-xs text-gray-500">@{log.username}</div>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            log.action === 'create' ? 'bg-green-100 text-green-800' :
                            log.action === 'update' ? 'bg-blue-100 text-blue-800' :
                            log.action === 'delete' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                          {log.resource_type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {log.resource_id ? `ID: ${log.resource_id.substring(0, 8)}...` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.ip_address || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {auditLogs.length === 0 && (
                  <div className="text-center py-8 text-gray-500">Aucun log d'audit trouvé</div>
                )}
              </div>

              {auditTotal > 0 && (
                <Pagination
                  placement="bottom"
                  currentPage={auditPage}
                  totalPages={auditTotalPages}
                  totalItems={auditTotal}
                  itemsPerPage={auditPageSize}
                  onPageChange={setAuditPage}
                  onPageSizeChange={(n) => {
                    setAuditPageSize(n);
                    setAuditPage(1);
                  }}
                />
              )}
            </div>
          )}

          {!loading && activeTab === 'sessions' && (() => {
            const totalActive = activeSessions.length;
            const sessionsTotalPagesAdmin = Math.max(1, Math.ceil(totalActive / sessionsPageSize));
            const pageRows = activeSessions.slice(
              (sessionsPage - 1) * sessionsPageSize,
              sessionsPage * sessionsPageSize
            );

            return (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-900">Sessions Actives</h2>
                <button
                  type="button"
                  onClick={loadData}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  title="Rafraîchir la liste"
                >
                  Rafraîchir
                </button>
              </div>

              <div className="text-sm text-gray-600">
                {totalActive > 0 ? (
                  <span>
                    Total : <strong>{totalActive}</strong> session{totalActive > 1 ? 's' : ''} active
                    {totalActive > 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Aucune session active</span>
                )}
              </div>

              <div className="rounded-xl border border-yellow-200 bg-yellow-50/80 px-4 py-3 text-xs text-yellow-900">
                Les sessions DB sont invalidées immédiatement. Les éventuels tokens JWT déjà émis
                resteront acceptés jusqu'à leur expiration ; la reconnexion est bloquée si le
                compte est désactivé.
              </div>

              {sessionsBackendMissing && (
                <div className="rounded-xl border border-orange-200 bg-orange-50/90 px-4 py-3 text-sm text-orange-900">
                  <p className="font-semibold">Backend API non à jour</p>
                  <p className="mt-1 text-xs">
                    L’endpoint <code className="font-mono">/admin/sessions/active</code> renvoie 404. Redémarrez le service
                    <code className="font-mono"> platform-api</code> pour activer le tableau enrichi (IP, OS, durée,
                    terminaison). La liste reste vide tant que le backend n’est pas relancé.
                  </p>
                </div>
              )}

              {totalActive > 0 && (
                <Pagination
                  placement="top"
                  currentPage={sessionsPage}
                  totalPages={sessionsTotalPagesAdmin}
                  totalItems={totalActive}
                  itemsPerPage={sessionsPageSize}
                  onPageChange={setSessionsPage}
                  onPageSizeChange={(n) => {
                    setSessionsPageSize(n);
                    setSessionsPage(1);
                  }}
                />
              )}

              <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse IP</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appareil / OS</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Localisation</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connexion</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pageRows.map((s) => {
                        const ua = parseUserAgent(s.user_agent);
                        const ipLookupUrl = s.ip_address
                          ? `https://ipinfo.io/${encodeURIComponent(s.ip_address)}`
                          : null;
                        const durationLabel = formatDurationSeconds(s.duration_seconds);
                        return (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 align-top">
                              <div className="text-sm font-medium text-gray-900">{s.display_name || '—'}</div>
                              <div className="text-xs text-gray-500">
                                {s.member_number ? <span className="font-mono">{s.member_number}</span> : null}
                                {s.member_number && s.username ? ' · ' : null}
                                {s.username ? <span>@{s.username}</span> : null}
                              </div>
                              {s.role ? (
                                <div className="text-[11px] text-gray-400 capitalize">{s.role}</div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 align-top text-sm text-gray-700 font-mono">
                              {s.ip_address || '—'}
                            </td>
                            <td className="px-4 py-3 align-top text-sm text-gray-700">
                              <div>{ua.device !== '—' ? ua.device : 'Bureau'}</div>
                              <div className="text-xs text-gray-500">
                                {[ua.os, ua.browser].filter((x) => x && x !== '—').join(' · ') || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top text-sm text-gray-700">
                              {ipLookupUrl ? (
                                <a
                                  href={ipLookupUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-entrea-royal hover:underline"
                                  title="Ouvrir la fiche IP sur ipinfo.io"
                                >
                                  Voir (ipinfo.io)
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 align-top text-sm text-gray-700 whitespace-nowrap">
                              {durationLabel}
                            </td>
                            <td className="px-4 py-3 align-top text-xs text-gray-500 whitespace-nowrap">
                              {s.login_time ? formatDate(s.login_time) : '—'}
                            </td>
                            <td className="px-4 py-3 align-top text-right">
                              <button
                                type="button"
                                disabled={terminatingUserId === s.user_id}
                                onClick={() => handleTerminateSession(s)}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-60"
                                title="Forcer la déconnexion (sessions DB invalidées)"
                              >
                                {terminatingUserId === s.user_id ? 'En cours…' : 'Terminer'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {pageRows.length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">Aucune session active</div>
                  )}
                </div>
              </div>

              <div className="md:hidden space-y-3">
                {pageRows.map((s) => {
                  const ua = parseUserAgent(s.user_agent);
                  const ipLookupUrl = s.ip_address
                    ? `https://ipinfo.io/${encodeURIComponent(s.ip_address)}`
                    : null;
                  const durationLabel = formatDurationSeconds(s.duration_seconds);
                  return (
                    <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{s.display_name || '—'}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {s.member_number ? <span className="font-mono">{s.member_number}</span> : null}
                            {s.member_number && s.username ? ' · ' : null}
                            {s.username ? <span>@{s.username}</span> : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={terminatingUserId === s.user_id}
                          onClick={() => handleTerminateSession(s)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-60 flex-shrink-0"
                        >
                          {terminatingUserId === s.user_id ? 'En cours…' : 'Terminer'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
                        <div>
                          <p className="text-gray-500">IP</p>
                          <p className="font-mono text-gray-800">{s.ip_address || '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Appareil</p>
                          <p className="text-gray-800">{ua.device !== '—' ? ua.device : 'Bureau'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">OS</p>
                          <p className="text-gray-800">{ua.os}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Durée</p>
                          <p className="text-gray-800">{durationLabel}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-500">Connexion</p>
                          <p className="text-gray-800">{s.login_time ? formatDate(s.login_time) : '—'}</p>
                        </div>
                        {ipLookupUrl ? (
                          <div className="col-span-2">
                            <a
                              href={ipLookupUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-entrea-royal hover:underline"
                            >
                              Voir la localisation (ipinfo.io)
                            </a>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {pageRows.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500 text-sm">
                    Aucune session active
                  </div>
                )}
              </div>

              {totalActive > 0 && (
                <Pagination
                  placement="bottom"
                  currentPage={sessionsPage}
                  totalPages={sessionsTotalPagesAdmin}
                  totalItems={totalActive}
                  itemsPerPage={sessionsPageSize}
                  onPageChange={setSessionsPage}
                  onPageSizeChange={(n) => {
                    setSessionsPageSize(n);
                    setSessionsPage(1);
                  }}
                />
              )}
            </div>
            );
          })()}

          {!loading && activeTab === 'system' && systemInfo && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Informations Système</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Système</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Plateforme:</span>
                      <span className="font-medium">{systemInfo.system?.platform || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Version Python:</span>
                      <span className="font-medium">{systemInfo.system?.python_version?.split(' ')[0] || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base de données:</span>
                      <span className="font-medium">{systemInfo.system?.database?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Taille (KB):</span>
                      <span className="font-medium">
                        {systemInfo.system?.database?.size 
                          ? (systemInfo.system.database.size / 1024).toFixed(2) 
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Heure serveur:</span>
                      <span className="font-medium">{formatDate(systemInfo.server_time || systemInfo.generated_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Collections</h3>
                  <div className="space-y-2 text-sm max-h-64 overflow-y-auto">
                    {Object.keys(systemCollections).length > 0 &&
                      Object.entries(systemCollections).map(([name, count]) => (
                        <div key={name} className="flex justify-between">
                          <span className="text-gray-600 capitalize">{name}:</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    {Object.keys(systemCollections).length === 0 && (
                      <div className="text-gray-500">Aucune information de collections disponible</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && activeTab === 'export' && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Export de Données</h2>
              <p className="text-gray-600">Exportez les données du projet courant au format JSON ou CSV (jusqu&apos;à 10&nbsp;000 enregistrements par type).</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ADMIN_EXPORT_TYPES.map((resource) => (
                  <div key={resource.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    <h3 className="font-medium text-gray-900">{resource.label}</h3>
                    {resource.id === 'transactions' && (
                      <p className="text-xs text-gray-500">
                        Données issues de Ledgera (écritures comptables du projet).
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleExport(resource.id, 'json')}
                        className="flex-1 bg-entrea-panel text-entrea-ink font-medium py-2 px-3 rounded-lg border border-entrea-sky/40 transition-colors hover:bg-entrea-panel"
                      >
                        💾 JSON
                      </button>
                      <button
                        onClick={() => handleExport(resource.id, 'csv')}
                        className="flex-1 bg-white text-entrea-ink font-medium py-2 px-3 rounded-lg border border-entrea-sky/40 transition-colors hover:bg-entrea-panel"
                      >
                        📄 CSV
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && activeTab === 'import' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Import de Données</h2>
                <p className="text-gray-600 mt-2">Importez des données depuis un fichier JSON ou CSV. Assurez-vous que le format correspond au type de données sélectionné.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { 
                    id: 'members', 
                    name: 'Membres',
                    required: ['name', 'email', 'phone', 'role', 'join_date'],
                    optional: ['status', 'commission'],
                    description: 'Format: JSON (tableau d\'objets) ou CSV (en-têtes = noms de champs).',
                    templateFile: {
                      name: 'membres_modele.csv',
                      body:
                        'name,email,phone,role,join_date,status,commission\n' +
                        'Jean Ndayisaba,jean@example.com,+25779000111,membre,2026-04-01,active,Commission sociale\n',
                    },
                  },
                  { 
                    id: 'users', 
                    name: 'Utilisateurs',
                    required: ['username', 'role'],
                    optional: ['phone', 'email', 'name', 'member_id'],
                    description: 'Format: JSON (tableau d\'objets) ou CSV. Mot de passe : valeur par défaut serveur (ENTREA_DEFAULT_MEMBER_PASSWORD), changement obligatoire à la première connexion.',
                    templateFile: {
                      name: 'utilisateurs_modele.csv',
                      body:
                        'username,role,phone,email,name,member_id\n' +
                        '+25779000222,membre,+25779000222,alice@example.com,Alice Uwimana,\n',
                    },
                  },
                  {
                    id: 'transactions',
                    name: 'Écritures comptables',
                    importDisabled: true,
                    required: [],
                    optional: [],
                    description:
                      "L'import de transactions Mongo est désactivé : les écritures hors cotisations se gèrent dans Ledgera (écran Finances / API /api/v1/ledger/transactions). Exportez les écritures depuis l'onglet Export (« Écritures Ledgera »). Les cotisations s'importent via la carte « Cotisations ».",
                  },
                  { 
                    id: 'contributions', 
                    name: 'Cotisations',
                    required: ['member_id', 'amount', 'month'],
                    optional: ['status', 'payment_method', 'notes', 'proof_documents'],
                    description: 'JSON ou CSV. month = YYYY-MM. member_id = identifiant du membre (colonne _id de l’export Membres CSV/JSON).',
                    templateFile: {
                      name: 'contributions_modele.csv',
                      body:
                        'member_id,amount,month,status,payment_method,notes\n' +
                        'REMPLACER_PAR_ID_MEMBRE_EXPORT_MEMBRES,15000,2026-04,pending,,\n',
                    },
                  },
                  { 
                    id: 'meetings', 
                    name: 'Rapports (réunion/activité)',
                    required: ['title', 'date', 'location'],
                    optional: ['participants', 'agenda', 'decisions', 'event_id', 'is_published'],
                    description: 'Format: JSON (tableau d\'objets) ou CSV. event_id est requis pour lier le document à une réunion ou activité.'
                  },
                  { 
                    id: 'documents', 
                    name: 'Documents',
                    required: ['title', 'category'],
                    optional: ['description', 'file_url', 'is_public'],
                    description: 'Format: JSON (tableau d\'objets) ou CSV. category est requis, file_url est optionnel.'
                  },
                  { 
                    id: 'events', 
                    name: 'Événements',
                    required: ['title', 'start_date'],
                    optional: ['description', 'end_date', 'location', 'type'],
                    description: 'Format: JSON (tableau d\'objets) ou CSV. Dates au format ISO (YYYY-MM-DDTHH:MM:SS). Type par défaut: "meeting".'
                  }
                ].map((resource) => (
                  <div key={resource.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{resource.name}</h3>
                      <p className="text-xs text-gray-500 mb-2">{resource.description}</p>

                      {!resource.importDisabled && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-entrea-royal hover:text-entrea-ink font-medium mb-1">
                            📋 Voir le format requis
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                            <div className="mb-2">
                              <span className="font-semibold text-gray-700">Champs requis:</span>
                              <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                                {resource.required.map((field) => (
                                  <li key={field} className="text-gray-600">
                                    {field}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {resource.optional.length > 0 && (
                              <div>
                                <span className="font-semibold text-gray-700">Champs optionnels:</span>
                                <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                                  {resource.optional.map((field) => (
                                    <li key={field} className="text-gray-500">
                                      {field}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {resource.required.length > 0 && (
                              <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                                <code className="text-xs text-blue-800">
                                  {`[{\n  "${resource.required[0]}": "...",\n  ...\n}]`}
                                </code>
                              </div>
                            )}
                          </div>
                        </details>
                      )}
                    </div>

                    {resource.importDisabled ? (
                      <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        Import non disponible pour ce type — utilisez Ledgera pour les écritures comptables.
                      </p>
                    ) : (
                      <>
                        {resource.templateFile && (
                          <button
                            type="button"
                            onClick={() =>
                              downloadTextFile(resource.templateFile.name, resource.templateFile.body)
                            }
                            className="w-full text-sm font-medium py-2 px-3 rounded-lg border border-entrea-sky/40 text-entrea-ink bg-white hover:bg-entrea-panel transition-colors"
                          >
                            Télécharger modèle CSV
                          </button>
                        )}

                        <input
                          type="file"
                          accept=".json,.csv"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleImport(resource.id, file);
                              e.target.value = '';
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-entrea-panel file:text-entrea-ink hover:file:bg-entrea-panel cursor-pointer"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-medium text-yellow-900 mb-2">⚠️ Avertissement</h3>
                <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                  <li>L'import ajoutera de nouvelles données sans supprimer les existantes</li>
                  <li>Assurez-vous que le format (JSON ou CSV) correspond au type de données</li>
                  <li>Les IDs seront automatiquement générés pour la plupart des ressources ; pour les cotisations, member_id doit exister (export membres)</li>
                  <li>Les doublons seront détectés et ignorés (basés sur email/phone pour membres/utilisateurs)</li>
                  <li>JSON : tableau d’objets <code className="bg-yellow-100 px-1 rounded">[{`{...}`}, {`{...}`}]</code> — CSV : première ligne = en-têtes de champs</li>
                </ul>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default AdminDashboard;

